import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DeferredChart } from "@/components/DeferredChart";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Target, TrendingUp, Package, Truck, PlayCircle, ShoppingBag, ClipboardList, DollarSign,
  RefreshCw, Building2, ShieldAlert, Thermometer, MessageSquareWarning, AlertTriangle,
} from "lucide-react";
import { inr } from "@/lib/utils-format";
import { ExecAIAssistant } from "@/components/ExecAIAssistant";

const BRAND_COLORS = ["#C05621", "#5C4033", "#8B7355", "#D69E2E"];

export default function ExecutiveDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/mfg/dashboard/executive");
      setData(r.data);
      setDenied(false);
    } catch (e) {
      if (e?.response?.status === 403) {
        setDenied(true);
      } else {
        toast.error(e?.response?.data?.detail || "Failed to load");
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = setInterval(load, 120_000);
    return () => clearInterval(id);
  }, []);

  const t = data?.totals || {};
  const brandData = Object.entries(data?.brand_split || {}).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));

  if (denied) {
    return (
      <div className="space-y-6" data-testid="executive-dashboard-page">
        <div>
          <div className="uc-label">Manufacturing · Executive View</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Executive Dashboard</h1>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-10 text-center max-w-2xl mx-auto" data-testid="exec-dashboard-denied">
          <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-amber-600" />
          <h2 className="font-[Outfit] text-xl font-semibold mb-1">Head Office access required</h2>
          <p className="text-sm text-[#5C4033]">The Executive Dashboard is available only to Head Office users. Please contact your admin if you believe this is an error, or head back to your Factory Dashboard.</p>
        </div>
      </div>
    );
  }


  return (
    <div className="space-y-6" data-testid="executive-dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="uc-label">Manufacturing · Executive View</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Executive Dashboard</h1>
          <p className="text-sm text-[#8B7355] mt-1">Chairman's consolidated view — all factories, sales, quality alerts, and brand split. Auto-refreshes every 2 min.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="exec-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {!data ? (
        <div className="text-center py-24 text-[#8B7355]">{loading ? "Loading executive view…" : "No data."}</div>
      ) : (
        <>
          {/* Totals row */}
          <section>
            <h2 className="uc-label text-[#5C4033] mb-3">Today's Totals — All Factories</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiTile label="Target Production" value={Math.round(t.production_today_target || 0)} icon={Target} tone="amber" testid="exec-kpi-target" />
              <KpiTile label="Actual Production" value={Math.round(t.production_today_actual || 0)} icon={TrendingUp} tone="emerald" testid="exec-kpi-actual" />
              <KpiTile label="FG Batches" value={t.batches_today || 0} icon={Package} tone="blue" testid="exec-kpi-batches" />
              <KpiTile label="Dispatches" value={t.dispatches_today || 0} icon={Truck} tone="blue" testid="exec-kpi-dispatches" />
              <KpiTile label="Machines Running" value={t.machines_running || 0} icon={PlayCircle} tone="emerald" testid="exec-kpi-machines" />
              <KpiTile label="Invoices Today" value={t.sales_today_count || 0} icon={ShoppingBag} tone="neutral" testid="exec-kpi-sales-count" />
              <KpiTile label="Sales Value" value={inr(t.sales_today_amount || 0)} icon={DollarSign} tone="emerald" testid="exec-kpi-sales-value" isText />
              <KpiTile label="Pending POs" value={t.pending_production_orders || 0} icon={ClipboardList} tone="amber" testid="exec-kpi-pending-orders" />
            </div>
          </section>

          {/* By-Factory table */}
          <section>
            <h2 className="uc-label text-[#5C4033] mb-3">Per-Factory Performance</h2>
            <div className="bg-white border border-[#EAE5D9] rounded-md overflow-x-auto">
              <table className="w-full text-sm" data-testid="exec-by-factory">
                <thead className="bg-[#FAF6EE] text-[#8B7355] text-[11px] uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Factory</th>
                    <th className="text-right px-3 py-2">Target</th>
                    <th className="text-right px-3 py-2">Actual</th>
                    <th className="text-right px-3 py-2">Efficiency</th>
                    <th className="text-right px-3 py-2">Batches</th>
                    <th className="text-right px-3 py-2">Dispatches</th>
                    <th className="text-right px-3 py-2">Machines Running</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_factory || []).map((f) => {
                    const eff = f.target_qty > 0 ? Math.round((f.actual_qty / f.target_qty) * 100) : 0;
                    return (
                      <tr key={f.branch_id} className="border-t border-[#EAE5D9]" data-testid={`exec-factory-${f.branch_id}`}>
                        <td className="px-3 py-2"><div className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-[#8B7355]" /> <b>{f.branch_name}</b></div></td>
                        <td className="px-3 py-2 text-right font-mono">{Math.round(f.target_qty || 0)}</td>
                        <td className="px-3 py-2 text-right font-mono">{Math.round(f.actual_qty || 0)}</td>
                        <td className="px-3 py-2 text-right">
                          <Badge className={`border-0 ${eff >= 90 ? "bg-emerald-100 text-emerald-800" : eff >= 70 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>{eff}%</Badge>
                        </td>
                        <td className="px-3 py-2 text-right">{f.batches_today || 0}</td>
                        <td className="px-3 py-2 text-right">{f.dispatches_today || 0}</td>
                        <td className="px-3 py-2 text-right">{f.machines_running || 0}</td>
                      </tr>
                    );
                  })}
                  {(!data.by_factory || data.by_factory.length === 0) && (
                    <tr><td colSpan={7} className="text-center py-8 text-[#8B7355]">No factories configured.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Charts row */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-[#EAE5D9] rounded-md p-4">
              <div className="uc-label mb-3">Top 10 SKUs Produced (Last 7 Days)</div>
              <DeferredChart height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.top_10_skus_produced_7d || []} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <XAxis type="number" fontSize={11} />
                    <YAxis dataKey="product" type="category" width={140} fontSize={11} />
                    <Tooltip />
                    <Bar dataKey="qty" fill="#C05621" name="Units Produced" />
                  </BarChart>
                </ResponsiveContainer>
              </DeferredChart>
              {(!data.top_10_skus_produced_7d || data.top_10_skus_produced_7d.length === 0) && (
                <div className="text-center py-6 text-[#8B7355] text-xs">No production in the last 7 days.</div>
              )}
            </div>

            <div className="bg-white border border-[#EAE5D9] rounded-md p-4">
              <div className="uc-label mb-3">Brand Split (Own vs Private-Label)</div>
              <DeferredChart height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={brandData} dataKey="value" nameKey="name" outerRadius={100} label={({ name, value }) => `${name}: ${value}`}>
                      {brandData.map((_, i) => <Cell key={i} fill={BRAND_COLORS[i % BRAND_COLORS.length]} />)}
                    </Pie>
                    <Legend />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </DeferredChart>
              {brandData.length === 0 && (
                <div className="text-center py-6 text-[#8B7355] text-xs">No branded products yet.</div>
              )}
            </div>
          </section>

          {/* Quality Alerts */}
          <section>
            <h2 className="uc-label text-[#5C4033] mb-3">Quality Alerts · Last 24 Hours</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <AlertTile label="Env Fails" value={data.quality_alerts_24h?.env_fails || 0} icon={Thermometer} tone="red" testid="exec-alert-env" />
              <AlertTile label="Holds" value={data.quality_alerts_24h?.holds || 0} icon={ShieldAlert} tone="amber" testid="exec-alert-hold" />
              <AlertTile label="CCP Fails" value={data.quality_alerts_24h?.ccp_fails || 0} icon={AlertTriangle} tone="red" testid="exec-alert-ccp" />
              <AlertTile label="Complaints" value={data.quality_alerts_24h?.complaints || 0} icon={MessageSquareWarning} tone="amber" testid="exec-alert-complaints" />
            </div>
          </section>

          <div className="text-[10px] text-[#8B7355] text-right">
            As of {data.as_of ? new Date(data.as_of).toLocaleString() : "—"}
          </div>
        </>
      )}
      {!denied && <ExecAIAssistant />}
    </div>
  );
}

function KpiTile({ label, value, icon: Icon, tone, testid, isText }) {
  const bg = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    red: "bg-red-50 border-red-200 text-red-800",
    neutral: "bg-white border-[#EAE5D9] text-[#2C1810]",
  }[tone] || "bg-white border-[#EAE5D9]";
  return (
    <div className={`border rounded-lg p-4 ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className={`font-[Outfit] font-semibold mt-1 ${isText ? "text-2xl" : "text-3xl"}`}>{value ?? 0}</div>
    </div>
  );
}

function AlertTile({ label, value, icon: Icon, tone, testid }) {
  const bg = value > 0
    ? (tone === "red" ? "bg-red-50 border-red-300 text-red-800" : "bg-amber-50 border-amber-300 text-amber-800")
    : "bg-white border-[#EAE5D9] text-[#8B7355]";
  return (
    <div className={`border rounded-lg p-4 ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="text-3xl font-[Outfit] font-semibold mt-1">{value ?? 0}</div>
      {value > 0 && <div className="text-[10px] mt-1 opacity-80">Attention needed</div>}
    </div>
  );
}
